import React, { useState, useEffect } from 'react';
import axios from 'axios';

function WebsiteList() {
  // Use a unique sessionStorage key for each form input
  const websiteUrlKey = 'websiteUrl';
  const recipientEmailKey = 'recipientEmail';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Initialize state from sessionStorage or an empty string
  const [websites, setWebsites] = useState([]);
  const [url, setUrl] = useState(() => sessionStorage.getItem(websiteUrlKey) || '');
  const [message, setMessage] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [email, setEmail] = useState(() => sessionStorage.getItem(recipientEmailKey) || '');
  const [recipientMessage, setRecipientMessage] = useState('');
  const [logs, setLogs] = useState([]);

  // Use useEffect to save form inputs to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem(websiteUrlKey, url);
  }, [url]);

  useEffect(() => {
    sessionStorage.setItem(recipientEmailKey, email);
  }, [email]);

  // --- Data Fetching Function ---
  const fetchData = async () => {
    try {
      const [websitesResponse, recipientsResponse, logsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/websitechecker/websites`),
        axios.get(`${BACKEND_URL}/api/websitechecker/recipients`),
        axios.get(`${BACKEND_URL}/api/websitechecker/logs`)
      ]);
      setWebsites(websitesResponse.data);
      setRecipients(recipientsResponse.data);
      setLogs(logsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // --- Website Management Functions ---
  const handleWebsiteSubmit = async (e) => {
    e.preventDefault();
    if (!url) {
      setMessage('URL cannot be empty.');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/websitechecker/websites`, { url });
      setUrl('');
      setMessage(`Website '${url}' added successfully! Initiating full crawl...`);
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while adding the website.';
      setMessage(`Error: ${errorMessage}`);
      console.error('Error adding website:', error);
    }
  };

  const handleWebsiteDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/websitechecker/websites/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting website:', error);
    }
  };
  
  // NEW: Function to manually recheck a website
  const handleWebsiteRecheck = async (id) => {
    try {
      setMessage('Rechecking website. This may take a few minutes...');
      await axios.post(`${BACKEND_URL}/api/websitechecker/websites/${id}/recheck`);
      setMessage('Recheck complete!');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred during recheck.';
      setMessage(`Error: ${errorMessage}`);
      console.error('Error rechecking website:', error);
    }
  };

  // --- Email Recipient Management Functions ---
  const handleRecipientSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setRecipientMessage('Email cannot be empty.');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/websitechecker/recipients`, { email });
      setEmail('');
      setRecipientMessage(`Recipient '${email}' added successfully!`);
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while adding the recipient.';
      setRecipientMessage(`Error: ${errorMessage}`);
      console.error('Error adding recipient:', error);
    }
  };

  const handleRecipientDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/websitechecker/recipients/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting recipient:', error);
    }
  };

  // --- Initial Data Fetching on Component Mount ---
  useEffect(() => {
    fetchData();
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-8">
      <header className="text-center mb-12">
        <h1 className="text-5xl font-bold text-blue-600 dark:text-blue-400">Site Alerter Dashboard</h1>
        <p className="mt-2 text-lg text-gray-600 dark:text-gray-300">Daily Website Reachability Checker</p>
      </header>

      <main className="container mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* Website Management Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Monitored Websites</h2>
          
          <form onSubmit={handleWebsiteSubmit} className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter website URL"
              className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 transition duration-300 ease-in-out">Add Website</button>
          </form>
          {message && <p className={`mb-4 text-sm font-medium ${message.startsWith('Error') ? 'text-red-500' : 'text-green-500'}`}>{message}</p>}

          <div className="overflow-x-auto">
            {websites.map(website => (
              <div key={website._id} className="p-4 mb-4 rounded-lg bg-gray-50 dark:bg-gray-700 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 break-all">{website.url}</h3>
                  <div className="flex gap-2">
                    {/* NEW: Recheck button */}
                    <button onClick={() => handleWebsiteRecheck(website._id)} className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-md shadow-md hover:bg-yellow-600 transition duration-300 ease-in-out">Recheck</button>
                    <button onClick={() => handleWebsiteDelete(website._id)} className="px-3 py-1 text-sm bg-red-500 text-white rounded-md shadow-md hover:bg-red-600 transition duration-300 ease-in-out">Delete</button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Last Checked: {new Date(website.lastChecked).toLocaleString()}</p>
                
                <h4 className="text-md font-semibold mt-4 mb-2">Pages:</h4>
                <ul className="space-y-1">
                  {website.pages && website.pages.length > 0 ? (
                    website.pages.map((page, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full ${page.status === 'OK' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <a href={page.url} className="text-gray-700 dark:text-gray-200 hover:underline break-all">{page.url}</a>
                        <span className={`${page.status === 'OK' ? 'text-green-600' : 'text-red-600'}`}>({page.status})</span>
                      </li>
                    ))
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No pages found or checked yet.</p>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Email Recipient Management Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Alert Recipients</h2>
          
          <form onSubmit={handleRecipientSubmit} className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email for alerts"
              className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 transition duration-300 ease-in-out">Add Recipient</button>
          </form>
          {recipientMessage && <p className={`mb-4 text-sm font-medium ${recipientMessage.startsWith('Error') ? 'text-red-500' : 'text-green-500'}`}>{recipientMessage}</p>}

          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {recipients.map((recipient) => (
              <li key={recipient._id} className="flex justify-between items-center py-2">
                <span className="text-sm font-medium">{recipient.email}</span>
                <button onClick={() => handleRecipientDelete(recipient._id)} className="text-red-500 hover:text-red-700 transition duration-300">Delete</button>
              </li>
            ))}
          </ul>
        </section>

        {/* Historical Logs Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 col-span-1 md:col-span-2 lg:col-span-1">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Recent Check History</h2>
          {logs.length > 0 ? (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log._id} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700 shadow-sm">
                  <p className="font-semibold text-gray-700 dark:text-gray-200">
                    Check Time: <span className="font-normal">{new Date(log.checkTime).toLocaleString()}</span>
                  </p>
                  <ul className="mt-2 text-sm space-y-1">
                    {log.results.map((result, index) => (
                      <li key={index}>
                        <strong>Website:</strong> <a href={result.url} className="hover:underline">{result.url}</a>
                        <ul className="ml-4 mt-1">
                          {result.pages && result.pages.map((page, pageIndex) => (
                            <li key={pageIndex} className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${page.status === 'OK' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                <span className="font-medium">{page.url}:</span>
                                <span className={`${page.status === 'OK' ? 'text-green-600' : 'text-red-600'}`}>
                                  {page.status}
                                </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No check history available.</p>
          )}
        </section>

      </main>
    </div>
  );
}

export default WebsiteList;
